/**
 * Public CMS header hook — fetches header configuration from the CMS API.
 * Used by StorefrontLayout to render dynamic header (logo, menu, announcement bar).
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface HeaderConfig {
  logo_url: string | null
  menu_id: number | null
  menu_slug: string | null
  announcement_bar_text: string
  announcement_bar_enabled: boolean
}

export interface UseCmsHeaderResult {
  headerConfig: HeaderConfig | null
  isLoading: boolean
  isError: boolean
}

// ─── Default values ───────────────────────────────────────────────────────────

const DEFAULT_HEADER: HeaderConfig = {
  logo_url: null,
  menu_id: null,
  menu_slug: null,
  announcement_bar_text: '',
  announcement_bar_enabled: false,
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCmsHeader(): UseCmsHeaderResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['cms-header'],
    queryFn: async () => {
      const { data: response } = await api.get<{ data: HeaderConfig }>('/cms/header')
      return response.data
    },
    staleTime: 30 * 1000, // 30 seconds — refetch frequently to pick up admin changes
    retry: 1,
  })

  return {
    headerConfig: data ?? DEFAULT_HEADER,
    isLoading,
    isError,
  }
}
